
import React, { useState } from 'react';
import StarterInfoModal from './StarterInfoModal';
import GrowthInfoModal from './GrowthInfoModal';
import FullyManagedInfoModal from './FullyManagedInfoModal';

const Pricing: React.FC = () => {
  const [isStarterModalOpen, setIsStarterModalOpen] = useState(false);
  const [isGrowthModalOpen, setIsGrowthModalOpen] = useState(false);
  const [isFullyManagedModalOpen, setIsFullyManagedModalOpen] = useState(false);

  const plans = [
    {
      name: "Starter",
      price: "169",
      setup: "299",
      description: "For straightforward websites and simple apps.",
      features: ["Hosting and maintenance", "Security updates", "Bug fixes", "Backups", "Email support"],
      target: "Websites, blogs, events, basic business apps",
      color: "blue",
      recommended: false
    },
    {
      name: "Growth",
      price: "269",
      setup: "599",
      description: "When you need small changes and flexibility.",
      features: ["Everything in Starter, plus:", "Up to 1 hour of updates per month", "Faster support", "Performance checks", "Basic analytics"],
      target: "Portals, booking systems, internal tools",
      color: "purple",
      recommended: true
    },
    {
      name: "Fully Managed",
      price: "569",
      setup: "1,099",
      description: "For applications that your business relies on.",
      features: ["Ongoing improvements", "New features", "Automation support", "Priority assistance"],
      target: "Member platforms, paid services, growing products",
      color: "slate",
      recommended: false
    }
  ];

  return (
    <section id="pricing" className="py-24 relative bg-white scroll-mt-24">
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <span className="text-blue-600 font-black uppercase tracking-widest text-xs mb-4 block">Pricing</span>
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">Transparent Costs</h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto font-semibold">
            Fixed monthly plans for your custom web application, with a one-time setup fee.
          </p>
        </div>

        {/* How It Works Section */}
        <div className="mb-20">
          <h3 className="text-center text-2xl font-black text-slate-900 mb-12 uppercase tracking-tighter">How It Works</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: "fa-drafting-compass", text: "We build your solution" },
              { icon: "fa-server", text: "We host and maintain it" },
              { icon: "fa-credit-card", text: "Simple monthly or project fees" },
              { icon: "fa-rocket", text: "We keep everything running" }
            ].map((step, i) => (
              <div key={i} className="flex flex-col items-center text-center p-6 bg-slate-50 rounded-3xl border border-slate-100 group hover:bg-blue-50 transition-colors">
                <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-blue-600 shadow-sm mb-4 group-hover:scale-110 transition-transform">
                  <i className={`fas ${step.icon}`}></i>
                </div>
                <p className="font-bold text-slate-800">{step.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Web App Pricing Section */}
        <div>
          <div className="flex items-center gap-4 mb-10">
             <div className="h-px bg-slate-200 flex-grow"></div>
             <h3 className="text-2xl md:text-3xl font-black text-slate-900 text-center uppercase tracking-tight">Custom Web Applications & Hosting</h3>
             <div className="h-px bg-slate-200 flex-grow"></div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {plans.map((plan, i) => (
              <div 
                key={i} 
                className={`relative flex flex-col p-8 md:p-10 rounded-[3rem] border-2 transition-all duration-500 hover:-translate-y-2 ${
                  plan.recommended 
                  ? 'bg-white border-blue-500 shadow-2xl shadow-blue-500/10 z-10 scale-105' 
                  : 'bg-slate-50 border-slate-100 hover:border-slate-300'
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-blue-600 text-white text-[10px] font-black rounded-full uppercase tracking-widest shadow-lg">
                    Most Popular
                  </div>
                )}
                
                <div className="mb-8">
                  <h4 className="text-2xl font-black text-slate-900 mb-2 uppercase tracking-tighter">{plan.name}</h4>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-black text-slate-900">From ${plan.price}</span>
                    <span className="text-slate-500 font-bold">/month</span>
                  </div>
                  <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6">One time Setup Fee From ${plan.setup}</p>
                  <p className="text-slate-600 font-semibold leading-relaxed">{plan.description}</p>
                </div>

                <div className="flex-grow space-y-4 mb-10">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Includes:</div>
                  {plan.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start gap-3">
                      <i className={`fas fa-check-circle mt-1 text-sm ${plan.recommended ? 'text-blue-500' : 'text-slate-400'}`}></i>
                      <span className="text-slate-700 font-bold text-sm leading-tight">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-8 border-t border-slate-200">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Good for:</div>
                  <p className="text-slate-800 font-black text-sm mb-8">
                    {plan.name === 'Starter' ? (
                      <button 
                        onClick={() => setIsStarterModalOpen(true)}
                        className="text-left text-blue-600 hover:text-blue-700 underline decoration-2 decoration-blue-200 hover:decoration-blue-600 underline-offset-4 transition-all outline-none"
                      >
                        {plan.target} <i className="fas fa-info-circle ml-1 text-xs opacity-70"></i>
                      </button>
                    ) : plan.name === 'Growth' ? (
                       <button 
                        onClick={() => setIsGrowthModalOpen(true)}
                        className="text-left text-blue-600 hover:text-blue-700 underline decoration-2 decoration-blue-200 hover:decoration-blue-600 underline-offset-4 transition-all outline-none"
                      >
                        {plan.target} <i className="fas fa-info-circle ml-1 text-xs opacity-70"></i>
                      </button>
                    ) : plan.name === 'Fully Managed' ? (
                       <button 
                        onClick={() => setIsFullyManagedModalOpen(true)}
                        className="text-left text-blue-600 hover:text-blue-700 underline decoration-2 decoration-blue-200 hover:decoration-blue-600 underline-offset-4 transition-all outline-none"
                      >
                        {plan.target} <i className="fas fa-info-circle ml-1 text-xs opacity-70"></i>
                      </button>
                    ) : (
                      plan.target
                    )}
                  </p>
                  <button 
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest transition-all ${
                    plan.recommended 
                    ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/20' 
                    : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}>
                    Select {plan.name}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 pt-12 mt-8">
            <div className="p-8 bg-slate-50 rounded-3xl">
              <h5 className="font-black text-slate-900 uppercase tracking-tighter mb-4">One-Time Setup Fee</h5>
              <p className="text-slate-600 font-semibold leading-relaxed">
                A one-time setup cost covers design and development depending on what’s needed for your web app.
              </p>
            </div>
            <div className="p-8 bg-slate-50 rounded-3xl">
              <h5 className="font-black text-slate-900 uppercase tracking-tighter mb-4">Minimum Term</h5>
              <p className="text-slate-600 font-semibold leading-relaxed">
                6 or 12 months to start for managed web apps. Month-to-month after.
              </p>
            </div>
          </div>
        </div>

      </div>

      <StarterInfoModal isOpen={isStarterModalOpen} onClose={() => setIsStarterModalOpen(false)} />
      <GrowthInfoModal isOpen={isGrowthModalOpen} onClose={() => setIsGrowthModalOpen(false)} />
      <FullyManagedInfoModal isOpen={isFullyManagedModalOpen} onClose={() => setIsFullyManagedModalOpen(false)} />
    </section>
  );
};

export default Pricing;
